/**
 * Setup dialogs: Draw Modes, Via Parameters and Keystroke Macros.
 *
 * These are the manual's Chapter 4 Setup commands. Draw Modes and Via
 * Parameters edit the document's own `Display::` and `DefaultVia::` records, so
 * they travel with the file exactly as the original stores them. Keystroke
 * macros are a per-user preference rather than file data, so they live in local
 * storage.
 */

import type { DfmProfile } from '@sircad/core/dfm/rules.ts';
import type { MacroStep } from '../macro.ts';
import type {
  CadDocument, DisplaySetting, FloodFillSetting, GerberOutSetting,
  NetScanSetting, PadType,
} from '@sircad/core/model.ts';
import { fromDisplay, toDisplay, type DisplayUnit } from '@sircad/core/units.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface SetupOptions {
  doc: CadDocument;
  units: DisplayUnit;
  /** Called after a change so the shell can redraw and mark the file dirty. */
  onChange(label: string): void;
}

/** File Parameters also needs the user's prefs, since that is where it saves. */
export interface FileParamOptions extends SetupOptions {
  prefs: Prefs;
  /** Called when a setting is pinned to the user, so the shell can persist it. */
  onPref(prefs: Prefs): void;
}

// ---------------------------------------------------------------------------
// Draw Modes
// ---------------------------------------------------------------------------

/**
 * The four modes CIRCAD draws each object class in (OGxyz03.cpp `grlinex`).
 * Lower modes were there for speed on slow hardware; outline is still the most
 * useful of them, because it shows what runs underneath a wide track or pad.
 */
const DRAW_MODES = [
  { value: 0, label: '0 — Centre line' },
  { value: 1, label: '1 — Outline' },
  { value: 2, label: '2 — Sparse fill' },
  { value: 3, label: '3 — Dense fill' },
];

/** Byte offsets of each object class within the packed `DrawModes` word. */
const MODE_SHIFT = { line: 0, pad: 8, text: 16 } as const;

export function showDrawModesDialog(opts: FileParamOptions): void {
  const d = opts.doc;
  // Anything set here is the user's from now on: it is written to the document
  // so the drawing updates, and pinned to the preferences so the next file to
  // be opened cannot take it away again.
  const pin = <K extends keyof DisplayPrefs>(key: K, value: DisplayPrefs[K]): void => {
    opts.prefs.display[d.kind][key] = value;
    opts.onPref(opts.prefs);
  };

  openDialog({
    title: 'Setup File Parameters',
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form';
      body.append(form);

      const get = (shift: number): number => ((d.display?.drawModes ?? 0x030303) >> shift) & 3;
      const set = (shift: number, v: number): void => {
        if (!d.display) return;
        // Replace just this class's two bits, leaving the rest of the word —
        // including the flags in the high byte — exactly as the file had it.
        d.display.drawModes = (d.display.drawModes & ~(3 << shift)) | ((v & 3) << shift);
        // And pin only this class, so choosing a line mode does not silently
        // freeze the pad and text modes for every file opened afterwards.
        pin(DRAW_MODE_KEY[shift as keyof typeof DRAW_MODE_KEY], v & 3);
      };

      for (const [name, shift] of Object.entries(MODE_SHIFT)) {
        const sel = document.createElement('select');
        for (const m of DRAW_MODES) {
          const o = document.createElement('option');
          o.value = String(m.value);
          o.textContent = m.label;
          sel.append(o);
        }
        sel.value = String(get(shift));
        sel.addEventListener('change', () => {
          set(shift, Number(sel.value));
          opts.onChange('Draw modes');
        });
        form.append(labelled(name[0]!.toUpperCase() + name.slice(1), sel));
      }

      // Grid dots and the display colours sit with the draw modes, as the
      // manual arranges them, because they are all one `Display::` record in
      // the file — settings of the drawing, not of the person editing it.
      const display = d.display;
      if (display) {
        const spacing = lengthInput(opts.units, display.dotSpacing, (v) => {
          display.dotSpacing = v;
          pin('dotSpacing', v);
          opts.onChange('Grid dots');
        });
        spacing.title = 'Zero removes the grid dots';
        form.append(labelled('Grid dots', spacing));

        const colour = (
          label: string, key: 'dotColor' | 'backgroundColor'
            | 'highlightColor' | 'attentionColor',
        ): void => {
          const input = document.createElement('input');
          input.type = 'color';
          input.value = `#${(display[key] >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
          input.addEventListener('input', () => {
            const v = parseInt(input.value.slice(1), 16);
            display[key] = v;
            pin(key, v);
            opts.onChange(`${label} colour`);
          });
          form.append(labelled(label, input));
        };
        colour('Dot', 'dotColor');
        colour('Background', 'backgroundColor');
        colour('Highlight', 'highlightColor');
        colour('Attention', 'attentionColor');
      }

      // --- Clearances.
      //
      // The original puts these on this same page, in three groups: Flood
      // Fill, Net Scan and Gerber Output (OG3.rc, the FileParameters dialog).
      // They are the design rules the board was drawn to, so unlike the draw
      // modes above they belong to the *file* and are never pinned to the
      // user — opening someone else's board must show that board's rules.
      const rules = ensureRules(d);

      group(form, 'Flood Fill');
      rule(form, opts, 'Flood line size', rules.flood.floodLine,
        (v) => { rules.flood.floodLine = v; });
      rule(form, opts, 'Flood to line clearance', rules.flood.floodToLine,
        (v) => { rules.flood.floodToLine = v; });
      rule(form, opts, 'Flood to pad clearance', rules.flood.floodToPad,
        (v) => { rules.flood.floodToPad = v; });
      rule(form, opts, 'Cross-hatch grid spacing', rules.flood.hatchGrid,
        (v) => { rules.flood.hatchGrid = v; });

      group(form, 'Net Scan');
      rule(form, opts, 'Ratnest line size', rules.scan.ratLine,
        (v) => { rules.scan.ratLine = v; });
      rule(form, opts, 'Line to line clearance', rules.scan.lineToLine,
        (v) => { rules.scan.lineToLine = v; });
      rule(form, opts, 'Line to pad clearance', rules.scan.lineToPad,
        (v) => { rules.scan.lineToPad = v; });
      rule(form, opts, 'Pad to pad clearance', rules.scan.padToPad,
        (v) => { rules.scan.padToPad = v; });

      group(form, 'Gerber Output');
      rule(form, opts, 'Mask pad swell', rules.gerber.maskPadSwell,
        (v) => { rules.gerber.maskPadSwell = v; });
      rule(form, opts, 'Paste pad shrink', rules.gerber.stencilPadShrink,
        (v) => { rules.gerber.stencilPadShrink = v; });
      rule(form, opts, 'Plane pad swell', rules.gerber.planePadSwell,
        (v) => { rules.gerber.planePadSwell = v; });
      rule(form, opts, 'Plane hole swell', rules.gerber.planeHoleSwell,
        (v) => { rules.gerber.planeHoleSwell = v; });
      rule(form, opts, 'Solid fill line size', rules.gerber.floodLineWidth,
        (v) => { rules.gerber.floodLineWidth = v; });

      const note = document.createElement('p');
      note.className = 'modal-hint';
      note.textContent =
        'Dense fill draws objects at their true width; the lighter modes draw '
        + 'them hollow, so you can see what runs underneath. Highlight is the '
        + 'colour of a grabbed item, attention the colour of a fault. Draw '
        + 'modes and colours are kept as yours and re-applied to every file you '
        + 'open; the clearances below them belong to the board and travel with '
        + 'the file, because they are what it was drawn to.';
      form.append(note);

      const row = buttons(form);
      // The way back: stop pinning these and let the next file supply its own.
      button(row, 'Use the file’s', () => {
        opts.prefs.display[d.kind] = {};
        opts.onPref(opts.prefs);
        handle.close();
        opts.onChange('File parameters follow the file again');
      });
      button(row, 'Close', () => handle.close(), true);
    },
  });
}

/**
 * The three rule records, created from the original's own defaults when the
 * file has none.
 *
 * A board written by an older tool, or one SirCAD made from scratch, may carry
 * no `NetScan::`/`FloodFill::`/`GerberOut::` record at all. Materialising them
 * here means the fields are editable rather than blank, and the values are the
 * ones CIRCAD itself writes — measured from the shipped designs, not invented.
 */
function ensureRules(d: CadDocument): {
  scan: NetScanSetting;
  flood: FloodFillSetting;
  gerber: GerberOutSetting;
} {
  const scan = (d.netScan ??= {
    lineToLine: 0.003, lineToPad: 0.003, padToPad: 0.01, ratLine: 0.011,
  });
  const flood = (d.floodFill ??= {
    floodToLine: 0.009, floodToPad: 0.013, floodLine: 0.011, hatchGrid: 0.05,
    targetLayer: 0, sourceLayers: [],
  });
  const gerber = (d.gerberOut ??= {
    maskPadSwell: 0.01, stencilPadShrink: 0.005, planePadSwell: 0.005,
    planeHoleSwell: 0.03, floodLineWidth: 0.002,
  });
  return { scan, flood, gerber };
}

/** A group heading, standing in for the original's group box. */
function group(form: HTMLElement, title: string): void {
  const h = document.createElement('div');
  h.className = 'part-group';
  h.textContent = title;
  form.append(h);
}

/** One clearance field: a length input that writes straight to the document. */
function rule(
  form: HTMLElement, opts: SetupOptions, label: string,
  value: number, set: (v: number) => void,
): void {
  const input = lengthInput(opts.units, value, (v) => {
    set(Math.max(0, v));
    opts.onChange(label);
  });
  form.append(labelled(`${label} (${opts.units})`, input));
}

// ---------------------------------------------------------------------------
// Via Parameters
// ---------------------------------------------------------------------------

/**
 * Setup Via Parameters: the pad and hole size Place Via uses. As the manual
 * notes, changing them does not disturb vias already placed.
 */
export function showViaParamsDialog(opts: SetupOptions & { onConfirm?(): void }): void {
  const d = opts.doc;
  const via = (d.defaults.via ??= { type: 'T1' as PadType, size: 0.05, hole: 0.02 });

  openDialog({
    title: 'Setup Via Parameters',
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form';
      body.append(form);

      // On a board a via belongs on the pad master, so it reaches every copper
      // layer; on a schematic it belongs on the signals layer.
      const layerSel = document.createElement('select');
      d.layers.forEach((l, i) => {
        const o = document.createElement('option');
        o.value = String(i);
        o.textContent = `${i}  ${l.name}`;
        layerSel.append(o);
      });
      layerSel.value = String(typeof via.layer === 'number' ? via.layer : 0);
      layerSel.addEventListener('change', () => { via.layer = Number(layerSel.value); });
      form.append(labelled('Layer', layerSel));

      const size = lengthInput(opts.units, via.size, (v) => { via.size = v; });
      form.append(labelled('Via size', size));
      const hole = lengthInput(opts.units, via.hole ?? 0, (v) => { via.hole = v; });
      form.append(labelled('Hole size', hole));

      const note = document.createElement('p');
      note.className = 'modal-hint';
      note.textContent = 'Vias already placed keep the size they were given.';
      form.append(note);

      const confirm = (): void => {
        handle.close();
        opts.onChange('Via parameters');
        opts.onConfirm?.();
      };
      const row = buttons(form);
      button(row, 'Cancel', () => handle.close());
      button(row, 'Confirm', confirm, true);
      handle.setAccept(confirm);
    },
  });
}

// ---------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------

/** Per-user settings that are not part of any document. */
export interface Prefs {
  /**
   * Swap what File ▸ Open and File ▸ Import do.
   *
   * SirCAD opens *projects* from web storage and imports *files* from disk. In
   * CIRCAD, Open always meant a design file on disk — so anyone coming from it
   * reaches for File ▸ Open expecting a .SCH/.PCB picker. With this on, Open
   * takes a file and Import takes a stored project.
   */
  legacyFileVerbs: boolean;
  /**
   * Manufacturing rule sets the user has saved.
   *
   * With the preferences rather than with the document, because a shop's
   * capabilities belong to whoever is using the program: you send every board
   * to the same two or three places. The *chosen* set is written onto the
   * document, so a design still records what it was checked against.
   */
  dfmProfiles?: DfmProfile[];
  /**
   * Projects opened lately, newest first, for File ▸ Re-Open.
   *
   * Projects rather than files. The original's list was filenames, and a
   * browser cannot reopen a path — it has no permission to, and the file it was
   * handed may be anywhere. A project has an id the account can be asked for,
   * so it is the only thing here that can actually be recalled.
   */
  recentProjects?: { id: number; name: string }[];
  /**
   * The install banner has been shown and answered.
   *
   * A preference rather than a session flag on purpose. A banner that comes
   * back every time you open your work is one you learn to close without
   * reading, and the offer is not lost by remembering the answer: the title-bar
   * button stands for as long as the browser says the app can be installed, and
   * Help ▸ Install as App asks for it outright.
   */
  installBannerSeen?: boolean;
  /**
   * The recorded macro, if one has been recorded.
   *
   * One, as the feature asks for. It lives with the preferences because a macro
   * is a way of working rather than part of any drawing — you record "place a
   * mounting hole and label it" once and use it on every board.
   */
  macro?: MacroStep[];
  /**
   * Restrict picking to the working layer, rather than to everything visible.
   *
   * Off, a click takes whatever is under it on any layer that is shown — which
   * is what you want most of the time. On, only the active layer answers, so a
   * track running beneath a poured plane can be grabbed without the plane
   * taking the click every time. Either way a hidden or locked layer is never
   * touched: what you cannot see, you cannot move by accident.
   */
  pickActiveLayerOnly: boolean;
  /**
   * Zoom In/Out with the mouse buttons: a click on empty space zooms in with
   * the left button and out with the right. The manual gates this on there
   * being "no function in effect", so it never competes with a command that
   * wants the click.
   */
  mouseButtonZoom: boolean;
  /**
   * Fit the layer table to a CIRCAD file's contents when it is opened.
   *
   * Off by default, and the default matters. Gerber and KiCad imports are
   * always fitted, because the stack there is ours to invent and a two-sided
   * board has no business showing four mid layers and two planes. A CIRCAD
   * file is different: it *states* its own layer table, so dropping the empty
   * layers changes the file's own declaration, and saving it back writes a
   * smaller stack than the one it arrived with. That is a fair trade when you
   * want a readable layer panel, and the wrong one when the file has to
   * survive a round trip through the original program.
   */
  fitCircadLayers: boolean;
  /**
   * Draw the marked block with a moving dashed outline.
   *
   * The original's Animated Block Perimeter, on out of the box. A thin static
   * rectangle over a busy sheet is invisible, and the block is the thing every
   * Block command is about to act on - but it is a moving thing on screen at
   * all times, and some people cannot bear those.
   */
  animatedBlock: boolean;
  /**
   * Draw the cross-hair three pixels wide rather than one.
   *
   * The original's Thick Cross-hair Cursor, also on out of the box. One pixel
   * at half opacity disappears into copper; three is what it draws, a line
   * either side of the middle one.
   */
  thickCrosshair: boolean;
  /** Chrome colour scheme — header, footer, panels and their text. */
  theme: ThemeName;
  /**
   * File Parameters the user has chosen, per editor.
   *
   * These live in the file as a `Display::` record, which means every design
   * carries its own — so opening one used to replace the draw modes and
   * colours the user had set, and there was no way to keep a preference. What
   * is recorded here is only what the user has actually changed, and it is
   * re-applied over any document that is loaded, imported or created. A field
   * nobody has touched still comes from the file.
   */
  display: { sch: DisplayPrefs; pcb: DisplayPrefs };
}

/**
 * The `Display::` fields a user may pin. Absent means "take the file's".
 *
 * The three draw modes are pinned **separately**, not as the packed word the
 * file stores them in. Pinning the whole word meant that changing, say, the
 * line mode also froze the pad and text modes at whatever the document open at
 * the time happened to use — and then every file opened afterwards inherited
 * all three. A board whose pad nibble said "dense fill / no hole" would go on
 * hiding every drill hole on every board, with nothing on screen to explain it
 * and a rebuild no help, because the setting lives in local storage.
 */
export type DisplayPrefs = Partial<Pick<DisplaySetting,
  'dotSpacing' | 'dotColor' | 'backgroundColor'
  | 'highlightColor' | 'attentionColor'>> & {
  /** Draw mode for lines, pads and text, 0-3 each, pinned one at a time. */
  drawLine?: number;
  drawPad?: number;
  drawText?: number;
};

/** Which preference key each class's bits live under. */
export const DRAW_MODE_KEY = {
  0: 'drawLine',
  8: 'drawPad',
  16: 'drawText',
} as const satisfies Record<number, keyof DisplayPrefs>;

/**
 * Put the user's File Parameters onto a document.
 *
 * Called wherever a document arrives or is made, so a load or import cannot
 * change a setting the user chose. Only the Setup dialog writes to the prefs
 * themselves, which is what "they remain until altered" requires.
 */
export function applyDisplayPrefs(doc: CadDocument, prefs: Prefs): void {
  const wanted = prefs.display?.[doc.kind];
  if (!wanted || !doc.display) return;

  // `drawModes` is the shape this used to be pinned in. Anyone who set a draw
  // mode before now has one in local storage, and letting it through would
  // keep overriding all three classes for every file — the very thing the
  // per-class keys exist to stop. Dropped on sight.
  const { drawLine, drawPad, drawText, drawModes: _legacy, ...plain } =
    wanted as DisplayPrefs & { drawModes?: number };
  Object.assign(doc.display, plain);

  // Splice only the classes the user has actually pinned into the file's own
  // word, so an untouched class keeps whatever the drawing asked for.
  let modes = doc.display.drawModes;
  const splice = (shift: number, value: number | undefined): void => {
    if (value === undefined) return;
    modes = (modes & ~(3 << shift)) | ((value & 3) << shift);
  };
  splice(0, drawLine);
  splice(8, drawPad);
  splice(16, drawText);
  doc.display.drawModes = modes;
}

/** The colour schemes offered, and the label each shows in Preferences. */
export const THEMES = [
  { id: 'dark', label: 'Dark (default)' },
  { id: 'slate', label: 'Slate' },
  { id: 'amber', label: 'Amber' },
  { id: 'light', label: 'Light' },
  { id: 'circad', label: 'CIRCAD — grey and navy' },
] as const;

export type ThemeName = typeof THEMES[number]['id'];

/**
 * Put the theme on the document root, where the stylesheet's overrides hang.
 * The default has no attribute, so it falls through to the base variables.
 */
export function applyTheme(theme: ThemeName): void {
  if (theme === 'dark') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', theme);
}

const PREFS_STORE = 'sircad.prefs';
// Mouse-button zoom is on out of the box: it only acts when no command wants
// the click, so it costs nothing when drawing and is the quickest way around a
// board. Turn it off in Preferences if a stray click zooming is unwelcome.
// File ▸ Open reaches for a design file, as it always has. SirCAD's own sense
// of Open — a project out of web storage — is the newer of the two meanings,
// and it is not the one somebody arriving from the original is reaching for:
// there, Open has meant a .SCH or .PCB on disk for twenty years. Import takes
// the project instead. Turn it off in Preferences for the web-first order.
const PREFS_DEFAULT: Prefs = {
  legacyFileVerbs: true, mouseButtonZoom: true, fitCircadLayers: false, theme: 'dark',
  dfmProfiles: [], recentProjects: [],
  pickActiveLayerOnly: false,
  // Both on, as the original has them: its preference for each reads 'anything
  // but No', so an absent key leaves the flag set.
  animatedBlock: true, thickCrosshair: true,
  display: { sch: {}, pcb: {} },
};

export function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_STORE);
    if (!raw) return freshPrefs();
    const parsed = JSON.parse(raw) as Partial<Prefs> | null;
    const merged = { ...PREFS_DEFAULT, ...(parsed ?? {}) };
    // The display block is nested, so a top-level spread would hand back the
    // shared default object and let one editor's settings write into it.
    merged.display = {
      sch: { ...(parsed?.display?.sch ?? {}) },
      pcb: { ...(parsed?.display?.pcb ?? {}) },
    };
    return merged;
  } catch {
    return freshPrefs();
  }
}

function freshPrefs(): Prefs {
  return { ...PREFS_DEFAULT, display: { sch: {}, pcb: {} } };
}

/** Persist preferences. Exported so the shell can save a File Parameters edit. */
export function savePrefs(p: Prefs): void {
  try { localStorage.setItem(PREFS_STORE, JSON.stringify(p)); } catch { /* private mode */ }
}

export function showPreferencesDialog(opts: {
  prefs: Prefs;
  onChange(prefs: Prefs): void;
}): void {
  const prefs: Prefs = { ...opts.prefs };

  openDialog({
    title: 'Setup Preferences',
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form';
      body.append(form);

      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = prefs.legacyFileVerbs;
      box.addEventListener('change', () => { prefs.legacyFileVerbs = box.checked; });
      form.append(labelled('CIRCAD file commands', box));

      const scope = document.createElement('input');
      scope.type = 'checkbox';
      scope.checked = prefs.pickActiveLayerOnly;
      scope.title = 'A hidden or locked layer is never pickable either way. '
        + 'Also on the status bar, beside the layer name.';
      scope.addEventListener('change', () => { prefs.pickActiveLayerOnly = scope.checked; });
      form.append(labelled('Pick only on the active layer', scope));

      const fit = document.createElement('input');
      fit.type = 'checkbox';
      fit.checked = prefs.fitCircadLayers;
      fit.title = 'Gerber and KiCad imports are always fitted; this is for '
        + 'CIRCAD files, which declare their own layer table.';
      fit.addEventListener('change', () => { prefs.fitCircadLayers = fit.checked; });
      form.append(labelled('Fit layers on opening a CIRCAD file', fit));

      // Mouse-button zoom is not here. It is one of the original's own Options
      // switches and now sits on that page (`S O`) with the other two, rather
      // than being offered twice under two different names.

      // Applied as you choose, so a scheme is judged on the real thing rather
      // than its name; Cancel puts back whatever was in force on opening.
      const started = opts.prefs.theme;
      const theme = document.createElement('select');
      for (const t of THEMES) {
        const o = document.createElement('option');
        o.value = t.id;
        o.textContent = t.label;
        theme.append(o);
      }
      theme.value = prefs.theme;
      theme.addEventListener('change', () => {
        prefs.theme = theme.value as ThemeName;
        applyTheme(prefs.theme);
      });
      form.append(labelled('Colours', theme));

      const note = document.createElement('p');
      note.className = 'modal-hint';
      note.textContent =
        'CIRCAD file commands: File ▸ Open meant a design file on disk, so with '
        + 'this on Open takes a .SCH/.PCB and Import opens a stored project — the '
        + 'other way round from the default. Mouse button zoom: clicking empty '
        + 'space zooms in with the left button and out with the right, whenever '
        + 'no command is waiting for that click.';
      form.append(note);

      const confirm = (): void => {
        handle.close();
        savePrefs(prefs);
        opts.onChange(prefs);
      };
      const row = buttons(form);
      button(row, 'Cancel', () => { applyTheme(started); handle.close(); });
      button(row, 'Confirm', confirm, true);
      handle.setAccept(confirm);
    },
  });
}

// ---------------------------------------------------------------------------
// Keystroke Macros
// ---------------------------------------------------------------------------

/** Key name → a run of command codes, e.g. `F3` → `ZX` or `PW`. */
export type MacroMap = Record<string, string>;

const MACRO_STORE = 'sircad.macros';

/**
 * The keys the manual makes user-definable (we cover the plain F-keys).
 *
 * F11 and F12 are not among them: they work the macro *recorder*, and a slot
 * offering to put a command on F11 was offering something the key handler had
 * already given away — it takes the recorder's two keys before the written
 * macros are looked at, so anything assigned here never ran.
 */
export const MACRO_KEYS = [
  'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10',
];

/**
 * CIRCAD's factory defaults, as close as our command set allows.
 *
 * Net Scan and Net Rename had F11 and F12 before the recorder took them. They
 * are not moved to another key — the free ones are free on purpose — and both
 * are two letters away at N S and N R, with the scan also on the status bar.
 */
const FACTORY: MacroMap = {
  F2: 'ZX', F3: 'ZW', F4: 'ZI', F5: 'ZO', F6: 'ZR',
  F7: 'GI', F8: 'PW', F9: 'PL', F10: 'PC',
};

/** Only the keys we still bind: a stored F11 would sit there unreachable. */
function usable(m: MacroMap): MacroMap {
  const out: MacroMap = {};
  for (const key of MACRO_KEYS) if (m[key]) out[key] = m[key];
  return out;
}

export function loadMacros(): MacroMap {
  try {
    const raw = localStorage.getItem(MACRO_STORE);
    if (!raw) return { ...FACTORY };
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object'
      ? usable(parsed as MacroMap) : { ...FACTORY };
  } catch {
    return { ...FACTORY };
  }
}

function saveMacros(m: MacroMap): void {
  try { localStorage.setItem(MACRO_STORE, JSON.stringify(m)); } catch { /* private mode */ }
}

export interface MacroDialogOptions {
  macros: MacroMap;
  /** Every command code the app implements, for validation and the picker. */
  known: { code: string; label: string }[];
  onChange(macros: MacroMap): void;
}

export function showMacrosDialog(opts: MacroDialogOptions): void {
  const macros: MacroMap = { ...opts.macros };
  const codes = new Set(opts.known.map((k) => k.code));

  openDialog({
    title: 'Setup Keystroke Macros',
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form';
      body.append(form);

      // A datalist of implemented commands, so the codes are discoverable
      // rather than something you have to remember from the manual.
      const list = document.createElement('datalist');
      list.id = 'macro-codes';
      for (const k of opts.known) {
        const o = document.createElement('option');
        o.value = k.code;
        o.label = `${k.code} — ${k.label}`;
        list.append(o);
      }
      form.append(list);

      const err = document.createElement('p');
      err.className = 'modal-error';
      err.hidden = true;

      for (const key of MACRO_KEYS) {
        const input = document.createElement('input');
        input.type = 'text';
        input.setAttribute('list', 'macro-codes');
        input.placeholder = '—';
        input.value = macros[key] ?? '';
        input.spellcheck = false;
        input.addEventListener('change', () => {
          const seq = input.value.trim().toUpperCase();
          if (!seq) { delete macros[key]; err.hidden = true; return; }
          const bad = seq.split(/\s+/).filter((c) => !codes.has(c));
          if (bad.length) {
            err.textContent = `Not a command: ${bad.join(' ')}`;
            err.hidden = false;
            return;
          }
          err.hidden = true;
          macros[key] = seq;
        });
        form.append(labelled(key, input));
      }

      form.append(err);

      const note = document.createElement('p');
      note.className = 'modal-hint';
      note.textContent =
        'Assign one or more command codes to a function key, separated by '
        + 'spaces — ZX for Zoom Extents, PW for a signal wire. F11 and F12 are '
        + 'not here: they record and play the macro recorder (S R, S B).';
      form.append(note);

      const row = buttons(form);
      button(row, 'Factory defaults', () => {
        handle.close();
        saveMacros({ ...FACTORY });
        opts.onChange({ ...FACTORY });
      });
      button(row, 'Cancel', () => handle.close());
      const save = (): void => {
        handle.close();
        saveMacros(macros);
        opts.onChange(macros);
      };
      button(row, 'Save', save, true);
      handle.setAccept(save);
    },
  });
}

// ---------------------------------------------------------------------------

function labelled(label: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'field';
  const span = document.createElement('span');
  span.textContent = label;
  wrap.append(span, control);
  return wrap;
}

/** A number input shown in the current display unit, stored in inches. */
function lengthInput(
  units: DisplayUnit, value: number, onChange: (v: number) => void,
): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.min = '0';
  // No step — see the note in edit.ts. Converting it along with the value kept
  // the two consistent, which only means the box refuses the same imported via
  // hole in either unit: four decimals are shown against a step of three.
  input.step = 'any';
  input.value = String(Number(toDisplay(value, units).toFixed(4)));
  input.addEventListener('change', () => {
    const v = fromDisplay(parseFloat(input.value), units);
    if (Number.isFinite(v) && v >= 0) onChange(v);
  });
  return input;
}

// ---------------------------------------------------------------------------
// Setup Options
// ---------------------------------------------------------------------------

/**
 * Setup ▸ Options (S O) — the switches the original keeps on this page.
 *
 * Its own is a tabbed `SystemParameters` sheet (OG3.rc:973) with three groups:
 * Initial Startup, User Preferences, and Explorer file associations. Two of the
 * three do not survive the move to a browser — a display number, a window
 * rectangle, a QuickLaunch tray icon and a list of file extensions to register
 * with the shell are all things the browser owns — so what is left is the
 * middle group, which is the one anybody actually changed.
 *
 * The three here are the ones with something behind them. The rest of the
 * original's twenty-one are either about desktop chrome that does not exist
 * (the four bar-visibility switches, the four pop-up help ones), about a
 * behaviour SirCAD does not have (Sound Blaster tones, log files to disk), or
 * are already settled elsewhere and better: Always use Grid Units is a stop on
 * the units cycle in the status bar, Un-Do beyond Save to Disk is simply how
 * undo works now, and Explorer File Open is the CIRCAD file commands switch on
 * Setup ▸ Preferences.
 *
 * Two of these three were decisions taken in the code with nobody asked. They
 * are the original's defaults and they are good ones, but a moving dashed
 * rectangle on screen at all times is not to everyone's taste and neither is a
 * fat cursor, so they are switches rather than opinions.
 */
export function showOptionsDialog(opts: {
  prefs: Prefs;
  onChange(prefs: Prefs): void;
}): void {
  const prefs: Prefs = { ...opts.prefs };

  openDialog({
    title: 'Setup Options',
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form';
      body.append(form);

      /** One switch, in the original's own wording. */
      const flag = (
        label: string, key: 'animatedBlock' | 'thickCrosshair' | 'mouseButtonZoom',
        title: string,
      ): void => {
        const box = document.createElement('input');
        box.type = 'checkbox';
        box.checked = prefs[key];
        box.title = title;
        box.addEventListener('change', () => {
          prefs[key] = box.checked;
          // Applied as it is switched, so the effect is judged on the real
          // thing rather than on its name. Cancel puts them all back.
          opts.onChange({ ...prefs });
        });
        form.append(labelled(label, box));
      };

      group(form, 'Display');
      flag('Animated block perimeter', 'animatedBlock',
        'The marked block is drawn with a moving dashed outline. Off, it is a '
        + 'plain one — which on a busy sheet can be hard to pick out.');
      flag('Thick cross-hair cursor', 'thickCrosshair',
        'Three pixels rather than one. A single pixel at half opacity '
        + 'disappears into copper.');

      group(form, 'Mouse');
      flag('Zoom in and out with the mouse buttons', 'mouseButtonZoom',
        'Only where no command wants the click, so it never competes with '
        + 'drawing or with putting something down.');

      const note = document.createElement('p');
      note.className = 'modal-hint';
      note.textContent =
        'The original keeps its startup window size, display number and file '
        + 'associations on this page too. A browser owns all three, so they are '
        + 'not here. Its Always use Grid Units is the fourth stop on the units '
        + 'field in the status bar, and its Un-Do beyond Save to Disk is simply '
        + 'how undo works here — it reaches back through saved states already.';
      form.append(note);

      const row = buttons(body);
      const confirm = (): void => {
        opts.onChange(prefs);
        handle.close();
      };
      button(row, 'Confirm', confirm, true);
      button(row, 'Cancel', () => {
        // Everything was applied live, so cancelling has to put back what was
        // in force when the page opened rather than merely close.
        opts.onChange({ ...opts.prefs });
        handle.close();
      });
      handle.setAccept(confirm);
    },
  });
}
